import axios, { AxiosError } from "axios";

import { COOKIES } from "@/app/_libs/constants/cookie";
// import { ROUTES } from "@/app/_libs/constants/routes";
import { env } from "../../env";

type RefreshTokenResponse = {
  data: { 
    accessToken?: string;
    refreshToken?: string;
  }
};

// Refresh token function - moved here to avoid circular dependency
const refreshTokenApi = async (): Promise<RefreshTokenResponse> => {
  try {
    const response = await axios.post(
      `${env.NEXT_PUBLIC_API_BASE_URL}/v1/auth/admin/refresh-token`,
      {},
      {withCredentials: true}
    );
    return response.data;
  } catch (error) {
    console.error("Refresh token error:", error);
    throw error;
  }
};

// Shared refresh token promise to prevent concurrent refresh calls
let refreshPromise: Promise<RefreshTokenResponse> | null = null;

// Create axios client
const axiosClient = axios.create({
  baseURL: env.NEXT_PUBLIC_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies in requests
});


const LOGIN_PATH = "/v1/auth/admin/login";


// Handle API errors
export const handleApiError = (error: AxiosError): Error => {
  // Prefer backend-provided message if present; handle varied shapes and strings
  const raw = error.response?.data as unknown;
  let backendMessage: string | undefined;
  if (raw && typeof raw === "object") {
    const obj = raw as {
      message?: string;
      error?: string;
      statusCode?: number;
      status?: string;
      data?: unknown;
      details?: unknown;
    };
    // Common shapes { message }, { error }, { data: { message } }
    backendMessage = obj.message || obj.error;
    if (!backendMessage && obj.data && typeof obj.data === "object") {
      const nested = obj.data as { message?: string; error?: string };
      backendMessage = nested.message || nested.error;
    }
    // Prisma/validation style arrays [{ message }]
    if (!backendMessage && Array.isArray(obj.data)) {
      const first = obj.data[0] as { message?: string; error?: string };
      backendMessage = first?.message || first?.error;
    }
  } else if (typeof raw === "string") {
    backendMessage = raw; // e.g., S3 XML/plain text
  } else if (Array.isArray(raw)) {
    const first = raw[0] as unknown as { message?: string; error?: string };
    backendMessage = first?.message || first?.error;
  }
  const statusText = error.response?.statusText;
  const apiError = new Error(backendMessage || statusText || error.message);
  if (error.response) {
    (apiError as Error & { status?: number; data?: unknown }).status = error.response.status;
    (apiError as Error & { status?: number; data?: unknown }).data = error.response.data;
  }
  // Bubble up axios/code info if available
  (apiError as Error & { code?: string }).code = error.code;
  // Best-effort safe logging without risking crashes/noise in production
  try {
    if (!backendMessage && raw != null) {
      const payload = typeof raw === "object" ? JSON.stringify(raw) : String(raw);
      if (process.env.NODE_ENV !== "production") {
        console.error("API error payload:", payload);
      }
    }
  } catch {
    // swallow any serialization/logging issues
  }
  return apiError;
};

// Universal cookie utilities that work on both client and server
export const getCookie = async (name: string): Promise<string | undefined> => {
  // Server-side: use Next.js cookies API with dynamic import
  if (typeof window === "undefined") {
    try {
      const { cookies } = await import("next/headers");
      const cookieStore = await cookies();
      return cookieStore.get(name)?.value;
    } catch {
      return undefined;
    }
  }

  // Client-side: use document.cookie
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return undefined;
};

export const setCookie = async (
  name: string,
  value: string,
  options: {
    httpOnly?: boolean;
    secure?: boolean;
    sameSite?: "strict" | "lax" | "none";
    maxAge?: number;
  } = {},
) => {
  // Server-side: use Next.js cookies API with dynamic import
  if (typeof window === "undefined") {
    try {
      const { cookies } = await import("next/headers");
      const cookieStore = await cookies();
      const { httpOnly = false, secure = false, sameSite = "lax", maxAge } = options;

      cookieStore.set(name, value, {
        httpOnly,
        secure: process.env.NODE_ENV === "production" ? secure : false,
        sameSite,
        maxAge,
        path: "/",
      });
    } catch {
      // Ignore cookie setting errors on server
    }
    return;
  }

  // Client-side: use document.cookie
  const { httpOnly = false, secure = false, sameSite = "lax", maxAge } = options;
  // Proactively remove any duplicates scoped to sub-paths before setting root path cookie
  try {
    const segments = location.pathname.split("/").filter(Boolean);
    let cumulative = "";
    for (const seg of segments) {
      cumulative += `/${seg}`;
      document.cookie = `${name}=; Max-Age=0; Path=${cumulative}; SameSite=Lax`;
    }
  } catch {
    // ignore
  }
  let cookieString = `${name}=${value}`;

  if (maxAge) cookieString += `; max-age=${maxAge}`;
  if (secure) cookieString += "; Secure";
  if (httpOnly) cookieString += "; HttpOnly";
  cookieString += `; SameSite=${sameSite}`;
  cookieString += "; Path=/";

  document.cookie = cookieString;
};

export const deleteCookie = async (name: string) => {
  // Server-side: use Next.js cookies API with dynamic import
  if (typeof window === "undefined") {
    try {
      const { cookies } = await import("next/headers");
      const cookieStore = await cookies();
      cookieStore.delete(name);
    } catch {
      // Ignore cookie deletion errors on server
    }
    return;
  }

  // Client-side: use document.cookie - delete at root and known subpaths
  try {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    const segments = location.pathname.split("/").filter(Boolean);
    let cumulative = "";
    for (const seg of segments) {
      cumulative += `/${seg}`;
      document.cookie = `${name}=; Max-Age=0; Path=${cumulative}; SameSite=Lax`;
    }
  } catch {
    // ignore
  }
};


// Request interceptor - attach FE-managed access token
axiosClient.interceptors.request.use(
  async (config) => {
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosError["config"] & { _retry?: boolean };

    // Skip token refresh for authentication endpoints
    const isAuthEndpoint =
      originalRequest.url?.includes(LOGIN_PATH) ||
      originalRequest.url?.includes("/v1/auth/users/login") ||
      originalRequest.url?.includes("/v1/auth/admin/refresh-token");


    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      try {
        // Ensure only one refresh request is in-flight; others wait for the same promise
        if (!refreshPromise) {
          refreshPromise = refreshTokenApi().finally(() => {
            refreshPromise = null;
          });
        }

        const response = await refreshPromise;

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

        await setCookie(COOKIES.ACCESS_TOKEN, newAccessToken ?? '', {
          httpOnly: false,
          secure: process.env.NODE_ENV === "production",
          sameSite: "lax",
          maxAge: 60 * 60 * 24 * 7, // 7 days
        });

        await setCookie(COOKIES.REFRESH_TOKEN, newRefreshToken ?? '', {
          httpOnly: false,
          secure: process.env.NODE_ENV === "production",
          sameSite: "lax",
          maxAge: 60 * 60 * 24 * 30, // 30 days
        });

        return axiosClient(originalRequest);
      } catch (refreshError) {
        const axiosError = refreshError as AxiosError;
        const formattedError = handleApiError(axiosError);

        // Only delete cookies if it's an authentication failure (401, 403, etc.)
        // Don't delete cookies for network errors (timeout, no connection, etc.)
        const isAuthFailure = axiosError.response && 
          (axiosError.response.status === 401 || axiosError.response.status === 403);
        
        if (isAuthFailure) {
          await deleteCookie(COOKIES.ACCESS_TOKEN);
          await deleteCookie(COOKIES.REFRESH_TOKEN);
        }

        return Promise.reject(formattedError);
      }
    }

    const formattedError = handleApiError(error);
    return Promise.reject(formattedError);
  },
);

export default axiosClient;
